import request from "@/lib/api/handler";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { QueryKeys } from "@/queries/utils";
import { toast } from "sonner";

import { MinimalCandidateStatus } from "@/types";
import { Opportunity } from "@/queries/candidate/opportunities";
import {
  ApplyOpportunityFormPart1Schema,
  ApplyOpportunityFormPart2Schema,
} from "./schema";

const useApplyForOpportunityMutation = (id: string, onSuccess?: () => void) => {
  const qc = useQueryClient();

  const { getAccessTokenSilently } = useAuth0();

  return useMutation({
    mutationFn: async (
      data: ApplyOpportunityFormPart1Schema & ApplyOpportunityFormPart2Schema,
    ) => {
      const token = await getAccessTokenSilently();

      const response = await request<MinimalCandidateStatus>({
        method: "POST",
        token,
        path: `/api/jobOpening/${id}/apply`,
        body: JSON.stringify(data),
      });

      return response;
    },
    onSuccess(data) {
      qc.setQueryData<Opportunity[]>(
        QueryKeys.candidateOpportunities,
        (old) => {
          if (!old) {
            return old;
          }

          return old.map((opportunity) =>
            opportunity.id === id
              ? {
                  ...opportunity,
                  candidateStatus: data,
                }
              : opportunity,
          );
        },
      );
      onSuccess?.();
      toast.success("Application received!");
    },
    onError() {
      toast.error(`Your application was not processed, please try again.`);
    },
  });
};

export default useApplyForOpportunityMutation;
